import { ThreadListPanel } from "@/app/Threads/Components/ThreadListPanel";
import { useThreads } from "@/app/Threads/hooks/useThreads";
import { useThreadUnreadCount } from "@/app/Threads/hooks/useThreadUnreadCount";
import { useFirstThreadId } from "@/app/Threads/hooks/useFirstThreadId";
import { useThreadSort } from "@/app/Threads/hooks/useThreadSort";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { useRef, useState } from "react";
import { useFillViewportHeight } from "@/app/Threads/hooks/useFillViewportHeight";
import { useDebouncedValue } from "@/lib/Hooks/useDebouncedValue";
import { ThreadStatusFilter } from "@/gql/graphql";
import { useNavigate, useParams } from "@tanstack/react-router";

export function ProDriverThreadsScreen() {
  const { supplierId } = useGlobalSupplier();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ThreadStatusFilter.Open);
  const debouncedSearch = useDebouncedValue(search, 300);
  const unreadCounts = {
    [ThreadStatusFilter.Open]: useThreadUnreadCount(
      supplierId,
      ThreadStatusFilter.Open,
    ),
    [ThreadStatusFilter.Closed]: useThreadUnreadCount(
      supplierId,
      ThreadStatusFilter.Closed,
    ),
  };
  const { sort, onSortChange } = useThreadSort();
  const { threads, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useThreads({
      supplier_id: supplierId ?? undefined,
      search: debouncedSearch,
      status,
      sort,
    });
  const containerRef = useRef<HTMLDivElement>(null);
  const height = useFillViewportHeight(containerRef);

  const { threadId } = useParams({ strict: false });
  const navigate = useNavigate();
  const getFirstThreadId = useFirstThreadId();
  const statusRequest = useRef(0);

  /**
   * The thread being read belongs to the tab it was opened from, so it is gone
   * from the list the moment the other tab is picked. Move to the top of the
   * new list instead of leaving the panel showing something it doesn't list.
   */
  function clearSelection() {
    navigate({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/messages",
    });
  }

  async function handleStatusChange(nextStatus: ThreadStatusFilter) {
    setStatus(nextStatus);

    if (!threadId) {
      return;
    }

    const request = ++statusRequest.current;

    // A failure leaves nothing to move to, same as an empty list.
    const firstThreadId = await getFirstThreadId({
      supplier_id: supplierId ?? undefined,
      search: debouncedSearch,
      status: nextStatus,
      sort,
    }).catch(() => undefined);

    // A later pick owns the selection: two of these can be in flight at once,
    // and they don't have to answer in order.
    if (request !== statusRequest.current) {
      return;
    }

    if (!firstThreadId) {
      clearSelection();
      return;
    }

    navigate({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/messages/$threadId",
      params: (prev) => ({ ...prev, threadId: firstThreadId }),
    });
  }

  return (
    <div
      ref={containerRef}
      style={height !== null ? { height: `${height}px` } : undefined}
      className="-mb-60 mt-4 flex h-[calc(100dvh-280px)] min-h-[400px] overflow-hidden rounded-lg border border-gray-200 bg-white"
    >
      <ThreadListPanel
        threads={threads}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        selectedId={threadId ?? null}
        onSelect={(thread) =>
          navigate({
            from: "/$tenant/$orgId/supplier/{-$supplierId}",
            to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/messages/$threadId",
            params: (prev) => ({ ...prev, threadId: thread.id }),
          })
        }
        onClearSelection={clearSelection}
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={handleStatusChange}
        unreadCounts={unreadCounts}
        sort={sort}
        onSortChange={onSortChange}
      />
    </div>
  );
}
